// RMA Wizard — Vanilla-JS State-Machine für Self-Service-Rückgabe/Umtausch/Garantie.
// Kommuniziert mit dem WMS über den Shopify App Proxy unter /apps/rma/api/*.
(function () {
  'use strict'

  const root = document.querySelector('.rma-page')
  if (!root) return

  const apiBase = window.location.origin
  const locale = root.dataset.locale || ''
  const customerId = root.dataset.customerId || ''
  const customerEmail = root.dataset.customerEmail || ''
  const customerName = root.dataset.customerName || ''
  let STR = {}
  try {
    STR = JSON.parse(root.dataset.strings || '{}')
  } catch (e) { /* leere Strings sind OK */ }

  // ── Step-Liste (für Progress-Bar) ───────────────────────────
  // Reihenfolge der Steps, die der Kunde durchläuft. CONFIRM/RESULT bekommen
  // 100% Progress; SELECT_ORDER ist optional je nach Auth.
  const PROGRESS_STEPS = ['IDENTIFY', 'SELECT_ORDER', 'SELECT_TYPE', 'SELECT_ITEMS', 'EXCHANGE_DETAILS', 'WARRANTY_PHOTO', 'WRONG_DELIVERY_DETAILS', 'RESOLUTION', 'SHOPIFY_EXCHANGE_ADDRESS', 'AMAZON_CONTACT', 'SHIPPING_OPTION', 'CONFIRM', 'RESULT']

  // Drei neue Top-Level-Faelle (Backend entscheidet selbst ueber Label/Ersatz):
  // WRONG_DELIVERY, MISSING_ITEM, PACKAGE_LOST. Sammeln Resolution-Wahl
  // (REPLACEMENT/REFUND), bei WRONG_DELIVERY zusaetzlich Freitext + Fotos.
  function isNewFlowType(t) {
    return t === 'WRONG_DELIVERY' || t === 'MISSING_ITEM' || t === 'PACKAGE_LOST'
  }

  // ── State ──────────────────────────────────────────────────
  // Persistenz: Wizard-State landet in sessionStorage, sodass ein Reload
  // den Kunden nicht zurück auf Schritt 1 wirft. sessionStorage statt
  // localStorage, damit Daten beim Tab-Schließen verschwinden — das ist
  // der erwartete „eine Sitzung lang" Scope und vermeidet veralteten State
  // beim nächsten Besuch.
  const STATE_KEY = 'rma:wizard:v1'
  const STATE_TTL_MS = 6 * 60 * 60 * 1000 // 6 h Hard-Cap als Sicherheitsnetz
  const PERSIST_KEYS = [
    'step', 'history', 'channelKey', 'type', 'identification', 'order', 'shipments',
    'selectedItems', 'exchangeDetails', 'photos', 'warrantyReasons',
    'amazonContact', 'shopifyExchangeAddress', 'shippingOption',
    'shippingFeeCents', 'autoRefund',
    'resolution', 'wrongDeliveryDescription', 'caseLevelPhotos',
    'idempotencyKey',
  ]

  const initialState = {
    step: 'IDENTIFY',
    history: [],
    channelKey: null,
    type: null,
    identification: null,
    order: null,
    // Alle Sendungen (WMS-Orders) der gewählten Bestellung. Eine Shopify-
    // Bestellung kann in mehrere Sendungen mit eigenem Versanddatum gesplittet
    // sein. Der SELECT_ITEMS-Step zeigt alle Sendungen gruppiert; state.order
    // wird auf die Sendung gesetzt, aus der der Kunde Artikel wählt (eine
    // Rücksendung = eine Sendung).
    shipments: [],
    selectedItems: [],
    exchangeDetails: {},
    photos: {},
    warrantyReasons: {},
    amazonContact: { email: '', name: '', address: null },
    // Optionaler Adress-Override für Shopify-Größentausch (default: Order-Adresse).
    shopifyExchangeAddress: null,
    shippingOption: null,
    shippingFeeCents: null,
    autoRefund: false,
    // WRONG_DELIVERY/MISSING_ITEM: Kunden-Wahl Ersatz oder Geld zurueck.
    resolution: null,
    // WRONG_DELIVERY: Freitext-Beschreibung, was stattdessen geliefert wurde.
    wrongDeliveryDescription: '',
    // WRONG_DELIVERY/MISSING_ITEM: Foto-URLs auf Case-Level (nicht pro Item).
    caseLevelPhotos: [],
    // Idempotenz-Schluessel — wird beim ersten Submit-Klick gesetzt und
    // mitpersistiert. Wenn der Browser zwischen POST und Result-Render
    // einbricht, deduplikiert der Server beim erneuten Klick anhand dieses
    // Keys statt einen zweiten Case anzulegen.
    idempotencyKey: null,
  }

  function loadPersistedState() {
    try {
      const raw = sessionStorage.getItem(STATE_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed || typeof parsed !== 'object') return null
      // TTL-Guard
      if (typeof parsed.savedAt !== 'number' || Date.now() - parsed.savedAt > STATE_TTL_MS) {
        sessionStorage.removeItem(STATE_KEY)
        return null
      }
      // RESULT-Schritt nicht wiederherstellen — wirkt sonst stale, der Vorgang
      // ist ja schon abgeschickt. IDENTIFY ist auch egal (Default).
      if (parsed.step === 'RESULT' || parsed.step === 'IDENTIFY') return null
      return parsed
    } catch { return null }
  }
  function persistState() {
    try {
      const snap = { savedAt: Date.now() }
      for (const k of PERSIST_KEYS) snap[k] = state[k]
      sessionStorage.setItem(STATE_KEY, JSON.stringify(snap))
    } catch { /* Quota o.ä. — ignorieren, Wizard läuft weiter */ }
  }
  function clearPersistedState() {
    try { sessionStorage.removeItem(STATE_KEY) } catch { /* ignore */ }
  }

  const persisted = loadPersistedState()
  const state = { ...initialState }
  if (persisted) {
    for (const k of PERSIST_KEYS) if (k in persisted) state[k] = persisted[k]
  }

  // ── Helpers ────────────────────────────────────────────────
  function url(path) {
    const u = new URL(apiBase + path)
    if (locale) u.searchParams.set('locale', locale)
    return u.toString()
  }
  // XHR statt fetch — auf manchen Storefronts wrappt ein Monitoring-Script
  // window.fetch und liefert bei Nicht-2xx-Antworten kein echtes Response,
  // sodass .json() auf undefined kracht ("Es ist ein Fehler aufgetreten" statt
  // der eigentlichen API-Antwort).
  function xhr(method, path, body) {
    return new Promise((resolve) => {
      const x = new XMLHttpRequest()
      x.open(method, url(path), true)
      if (body !== undefined) x.setRequestHeader('Content-Type', 'application/json')
      x.responseType = 'json'
      x.onload = () => {
        // Auch 4xx/5xx als Body durchreichen, damit der Aufrufer die
        // {ok:false,error:'…'}-Struktur sieht statt nur 'Fehler'.
        resolve(x.response ?? { ok: false, error: 'http ' + x.status })
      }
      x.onerror = () => resolve({ ok: false, error: 'network' })
      x.send(body !== undefined ? JSON.stringify(body) : null)
    })
  }
  async function post(path, body) { return xhr('POST', path, body) }
  async function get(path) { return xhr('GET', path) }
  function show(step) {
    if (state.step !== step) state.history.push(state.step)
    state.step = step
    render()
    persistState()
  }
  function back() {
    const prev = state.history.pop() ?? fallbackPrevStep()
    if (prev) state.step = prev
    render()
    persistState()
  }
  // Wenn die History fehlt (z.B. nach Cleanup oder wenn ein neuer Step
  // direkt eingenommen wurde), leiten wir den vorigen Schritt aus dem
  // aktuellen ab. Reihenfolge passt zum normalen Wizard-Flow.
  function fallbackPrevStep() {
    const detailsVals = Object.values(state.exchangeDetails)
    const hasSize = state.type === 'EXCHANGE'
      && detailsVals.some((d) => d.exchangeMode === 'SAME_PRODUCT_DIFFERENT_VARIANT')
    // EXCHANGE_DETAILS wurde übersprungen, wenn alle Items VOUCHER sind.
    const exchangeDetailsShown = state.type === 'EXCHANGE'
      && (hasSize || (detailsVals.length === 0))
    const isAmazon = state.channelKey === 'amazon'
    function prevAfterItems() {
      if (state.type === 'EXCHANGE') return exchangeDetailsShown ? 'EXCHANGE_DETAILS' : 'SELECT_ITEMS'
      if (state.type === 'WARRANTY') return 'WARRANTY_PHOTO'
      return 'SELECT_ITEMS'
    }
    switch (state.step) {
      case 'CONFIRM':
        if (isNewFlowType(state.type)) {
          if (state.type === 'PACKAGE_LOST') return 'SELECT_TYPE'
          if (state.type === 'WRONG_DELIVERY') return 'WRONG_DELIVERY_DETAILS'
          return 'RESOLUTION'
        }
        return 'SHIPPING_OPTION'
      case 'SHIPPING_OPTION':
        if (isAmazon) return 'AMAZON_CONTACT'
        if (hasSize) return 'SHOPIFY_EXCHANGE_ADDRESS'
        return prevAfterItems()
      case 'AMAZON_CONTACT':
      case 'SHOPIFY_EXCHANGE_ADDRESS':
        return prevAfterItems()
      case 'WARRANTY_PHOTO':
      case 'EXCHANGE_DETAILS':
        return 'SELECT_ITEMS'
      case 'RESOLUTION':
        // RESOLUTION gibt es nur noch fuer MISSING_ITEM (WRONG_DELIVERY
        // ueberspringt den Schritt) → zurueck zur Item-Auswahl.
        return 'SELECT_ITEMS'
      case 'WRONG_DELIVERY_DETAILS':
        return 'SELECT_ITEMS'
      case 'SELECT_ITEMS':
        return 'SELECT_TYPE'
      case 'SELECT_TYPE':
        return state.identification?.mode === 'shopify-login' ? 'SELECT_ORDER' : 'IDENTIFY'
      default:
        return null
    }
  }
  // Sicherheitsnetz: bei Tab-Wechsel/Reload nochmal persistieren, falls
  // zwischendurch state-Felder direkt verändert wurden ohne show()-Trigger.
  // Bei „Anderen Fall anmelden" wird isRestarting gesetzt, damit der
  // gerade gelöschte State nicht wieder geschrieben wird.
  let isRestarting = false
  window.addEventListener('beforeunload', () => {
    if (isRestarting) return
    try { persistState() } catch {}
  })
  // Tracking welcher Button gerade busy ist — wird gegen einen Spinner
  // ersetzt. Das Overlay-"Wird geladen…" entfällt damit.
  let busyBtn = null
  function setBusy(btn, on) {
    if (on) {
      if (!btn) return
      busyBtn = btn
      btn.dataset.origLabel = btn.textContent
      btn.disabled = true
      btn.classList.add('rma-btn--busy')
      btn.innerHTML = '<span class="rma-spinner" aria-hidden="true"></span>'
    } else {
      const target = btn || busyBtn
      if (!target) return
      target.disabled = false
      target.classList.remove('rma-btn--busy')
      if (target.dataset.origLabel != null) {
        target.textContent = target.dataset.origLabel
        delete target.dataset.origLabel
      }
      busyBtn = null
    }
  }
  // Backwards-compat alias — alte Aufrufer ohne Button-Referenz werden
  // nach und nach migriert. Der Spinner erscheint jetzt am gerade aktiven
  // Button (busyBtn), nicht mehr im Overlay.
  function setLoading(on) {
    const el = root.querySelector('[data-target="loading"]')
    if (el) el.hidden = true // Overlay komplett aus.
    if (!on) setBusy(null, false)
  }
  function showError(target, msg) {
    const el = root.querySelector(`[data-error="${target}"]`)
    if (el) el.textContent = msg
  }

  function render() {
    root.querySelectorAll('.rma-step').forEach((el) => {
      el.hidden = el.dataset.step !== state.step
    })
    updateProgress()
    if (state.step === 'SELECT_ORDER') renderOrders()
    if (state.step === 'SELECT_TYPE') renderTypeButtons()
    if (state.step === 'SELECT_ITEMS') renderItems()
    if (state.step === 'EXCHANGE_DETAILS') renderExchange()
    if (state.step === 'WARRANTY_PHOTO') renderPhotos()
    if (state.step === 'WRONG_DELIVERY_DETAILS') renderWrongDeliveryDetails()
    if (state.step === 'RESOLUTION') renderResolution()
    if (state.step === 'SHOPIFY_EXCHANGE_ADDRESS') renderShopifyExchangeAddress()
    if (state.step === 'AMAZON_CONTACT') renderAmazonContact()
    if (state.step === 'SHIPPING_OPTION') renderShipping()
    if (state.step === 'CONFIRM') renderSummary()
  }

  // Liefert die für den aktuellen Vorgang tatsächlich relevanten Steps.
  // Übersprungene Schritte (z.B. EXCHANGE_DETAILS bei reinem Voucher,
  // AMAZON_CONTACT bei Shopify, SHOPIFY_EXCHANGE_ADDRESS außer bei
  // Shopify-Größentausch) werden hier weggelassen. Damit zählt der
  // Progress-Balken nur, was der Kunde wirklich ausfüllt.
  function relevantSteps() {
    const out = []
    // Identifikation
    if (state.identification?.mode === 'shopify-login') {
      out.push('SELECT_ORDER')
    } else {
      out.push('IDENTIFY')
    }
    out.push('SELECT_TYPE')

    // Neue Top-Level-Faelle: WRONG_DELIVERY/MISSING_ITEM brauchen SELECT_ITEMS
    // + RESOLUTION, WRONG_DELIVERY zusaetzlich Detail-Step. PACKAGE_LOST geht
    // direkt von SELECT_TYPE zu CONFIRM (keine Items, keine Resolution).
    if (isNewFlowType(state.type)) {
      if (state.type === 'PACKAGE_LOST') {
        out.push('CONFIRM')
        out.push('RESULT')
        return out
      }
      out.push('SELECT_ITEMS')
      if (state.type === 'WRONG_DELIVERY') {
        // Falschlieferung: nur Items + Detail-Step (Freitext/Fotos), danach
        // direkt CONFIRM. Keine RESOLUTION-Wahl — der Fall ist zu speziell und
        // wird komplett manuell bearbeitet (Backend → MANUAL_REVIEW).
        out.push('WRONG_DELIVERY_DETAILS')
      } else {
        out.push('RESOLUTION')
      }
      out.push('CONFIRM')
      out.push('RESULT')
      return out
    }

    out.push('SELECT_ITEMS')

    // Sub-Schritte je Vorgangstyp
    if (state.type === 'EXCHANGE') {
      const detailsVals = Object.values(state.exchangeDetails)
      const hasSizeExchange = detailsVals.some((d) => d.exchangeMode === 'SAME_PRODUCT_DIFFERENT_VARIANT')
      const allVoucher = detailsVals.length > 0 && detailsVals.every((d) => d.exchangeMode === 'VOUCHER')
      // EXCHANGE_DETAILS taucht nur auf, wenn auch wirklich eine Auswahl
      // möglich war (Größentausch + Voucher) — bei reinem Voucher haben
      // wir den Schritt übersprungen.
      const onDetails = state.step === 'EXCHANGE_DETAILS'
      if (onDetails || hasSizeExchange || (!allVoucher && detailsVals.length === 0 && state.step !== 'SELECT_ITEMS')) {
        out.push('EXCHANGE_DETAILS')
      }
    } else if (state.type === 'WARRANTY') {
      out.push('WARRANTY_PHOTO')
    }

    // Adress-Schritt
    if (state.channelKey === 'amazon') {
      out.push('AMAZON_CONTACT')
    } else {
      const hasSizeExchange = state.type === 'EXCHANGE'
        && Object.values(state.exchangeDetails).some((d) => d.exchangeMode === 'SAME_PRODUCT_DIFFERENT_VARIANT')
      if (hasSizeExchange) out.push('SHOPIFY_EXCHANGE_ADDRESS')
    }

    out.push('SHIPPING_OPTION')
    out.push('CONFIRM')
    out.push('RESULT')
    return out
  }

  function updateProgress() {
    const wrap = root.querySelector('[data-target="progress"]')
    const fill = root.querySelector('[data-target="progress-fill"]')
    const label = root.querySelector('[data-target="progress-label"]')
    if (!wrap || !fill || !label) return
    if (state.step === 'IDENTIFY') {
      wrap.hidden = true
      return
    }
    const steps = relevantSteps()
    let idx = steps.indexOf(state.step)
    if (idx < 0) {
      // Aktueller Schritt nicht in relevantSteps (z.B. EXCHANGE_DETAILS
      // gerade beim Befüllen) — dann zähle bis zum nächst-passenden Schritt.
      idx = Math.max(1, PROGRESS_STEPS.indexOf(state.step))
    }
    // IDENTIFY/SELECT_ORDER zählen nicht als „Schritt 1" — wir starten
    // ab dem ersten realen Wizard-Schritt.
    const totalCount = steps.length - 1 // ohne IDENTIFY/SELECT_ORDER
    const number = idx // 0-basiert; SELECT_TYPE = 1
    const pct = Math.round((number / totalCount) * 100)
    wrap.hidden = false
    fill.style.width = pct + '%'
    const tpl = STR.progress_label || 'Schritt {step} / {total}'
    label.textContent = tpl.replace('{step}', number).replace('{total}', totalCount)
  }

  // ── Init / Routing ─────────────────────────────────────────
  const shop = (root.dataset.shop || '').toLowerCase()
  const defaultChannelKey = shop.includes('viamundi') ? 'shopify-viamundi' : 'shopify-getdigital'

  setLoading(true)
  if (persisted && state.step && state.step !== 'IDENTIFY') {
    if (state.step === 'SELECT_ORDER') {
      // Order-Liste wird nicht persistiert (nur state.step). Bei Restore die
      // Liste frisch holen, sonst bleibt der Container leer. state.order
      // explizit verwerfen, falls darin noch eine veraltete fremde Order
      // aus der Pre-GID-Backfill-Zeit klebt.
      state.order = null
      state.channelKey = state.channelKey || defaultChannelKey
      state.identification = state.identification || { mode: 'shopify-login' }
      loadMyOrders()
    } else {
      // Persistierten Stand wiederherstellen — kein Auto-Flow auslösen,
      // sonst überschreiben wir den State.
      render()
      setLoading(false)
    }
  } else if (customerId) {
    state.channelKey = defaultChannelKey
    state.identification = { mode: 'shopify-login' }
    loadMyOrders()
  } else {
    show('IDENTIFY')
    setLoading(false)
  }

  // ── IDENTIFY ───────────────────────────────────────────────
  root.querySelectorAll('.rma-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.tab
      root.querySelectorAll('.rma-tab').forEach((t) => {
        t.setAttribute('aria-selected', t.dataset.tab === target ? 'true' : 'false')
      })
      root.querySelectorAll('[data-form]').forEach((f) => {
        if (f.dataset.form === 'shopify' || f.dataset.form === 'amazon') {
          f.hidden = f.dataset.form !== target
        }
      })
    })
  })

  root.querySelector('[data-form="shopify"]').addEventListener('submit', async (e) => {
    e.preventDefault()
    const btn = e.target.querySelector('button[type="submit"]')
    const fd = new FormData(e.target)
    const orderNumber = (fd.get('orderNumber') || '').toString().trim()
    const email = (fd.get('email') || '').toString().trim()
    setBusy(btn, true)
    showError('shopify', '')
    state.channelKey = defaultChannelKey
    state.identification = { mode: 'shopify-orderemail', orderNumber, email }
    const r = await post('/apps/rma/api/lookup-shopify', { orderNumber, email })
    setBusy(btn, false)
    if (!r.ok) {
      showError('shopify', r.error === 'order_not_found'
        ? (STR.err_order_not_found || 'Bestellung nicht gefunden. Bitte prüfe Bestellnummer und E-Mail. Bei Fragen wende dich gerne an support@getdigital.de.')
        : (STR.err_generic || 'Es ist ein Fehler aufgetreten. Bitte versuche es später erneut oder schreibe an support@getdigital.de.'))
      return
    }
    // Eine Bestellnummer kann mehrere Sendungen liefern (Backend: shipments).
    // Fallback auf das einzelne `order` für älteres Backend.
    state.shipments = (Array.isArray(r.shipments) && r.shipments.length) ? r.shipments : (r.order ? [r.order] : [])
    state.order = state.shipments[0] || r.order
    show('SELECT_TYPE')
  })

  root.querySelector('[data-form="amazon"]').addEventListener('submit', async (e) => {
    e.preventDefault()
    const btn = e.target.querySelector('button[type="submit"]')
    const fd = new FormData(e.target)
    const amazonOrderId = (fd.get('amazonOrderId') || '').toString().trim()
    setBusy(btn, true)
    showError('amazon', '')
    state.channelKey = 'amazon'
    state.identification = { mode: 'amazon-order', amazonOrderId }
    const r = await post('/apps/rma/api/lookup-amazon', { amazonOrderId, locale })
    setBusy(btn, false)
    if (!r.ok) {
      // Harte FBA-Ablehnung: Amazon ist hier allein zuständig — kein Weiter,
      // keine Items. Text kommt vom Backend (alle Sprachen lokalisiert),
      // Fallback ist das Section-Setting.
      if (r.error === 'fba_not_supported') {
        showError('amazon', r.message || STR.err_fba_not_supported || 'Diese Bestellung wurde direkt von Amazon (Fulfilled by Amazon) versandt. Bitte wende dich direkt an den Amazon-Kundenservice.')
        return
      }
      showError('amazon', r.error === 'order_not_found'
        ? (STR.err_order_not_found || 'Bestellung nicht gefunden. Bitte prüfe Bestellnummer und E-Mail. Bei Fragen wende dich gerne an support@getdigital.de.')
        : (STR.err_generic || 'Es ist ein Fehler aufgetreten. Bitte versuche es später erneut oder schreibe an support@getdigital.de.'))
      return
    }
    // Amazon: immer genau eine Order, keine Sendungs-Gruppierung.
    state.order = r.order
    state.shipments = r.order ? [r.order] : []
    show('SELECT_TYPE')
  })

  // ── SELECT_ORDER (eingeloggt) ──────────────────────────────
  async function loadMyOrders() {
    show('SELECT_ORDER')
    const wrap = root.querySelector('[data-target="orders"]')
    if (wrap) wrap.innerHTML = '<div class="rma-loading"></div>'
    setLoading(true)
    const r = await get('/apps/rma/api/my-orders')
    setLoading(false)
    if (!r.ok || !r.orders || r.orders.length === 0) {
      // Kein Login-Treffer (z.B. cookies blockiert oder keine Bestellungen) →
      // Gast-Identifikation anbieten
      show('IDENTIFY')
      return
    }
    // Auch bei genau 1 Order die Liste rendern — sonst springt der Wizard
    // ohne sichtbare Order auf SELECT_TYPE und der Kunde sieht nicht, fuer
    // welche Bestellung er einen Vorgang anlegt.
    renderOrdersList(r.orders)
  }

  function renderOrdersList(orders) {
    const wrap = root.querySelector('[data-target="orders"]')
    wrap.innerHTML = ''
    // Eine Shopify-Bestellung kann als mehrere Sendungen (WMS-Orders) mit
    // identischer Bestellnummer auftauchen — sonst sähe der Kunde zwei
    // ununterscheidbare Buttons. Nach Bestellung gruppieren (shopifyOrderGid,
    // Fallback orderNumber) und je Bestellung EINEN Button zeigen; die
    // Sendungen kommen erst im SELECT_ITEMS-Step zum Vorschein.
    const groups = []
    const byKey = new Map()
    for (const o of orders) {
      const key = o.shopifyOrderGid || o.orderNumber || o.externalId || o.orderId
      let g = byKey.get(key)
      if (!g) { g = []; byKey.set(key, g); groups.push(g) }
      g.push(o)
    }
    for (const group of groups) {
      const head = group[0]
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'rma-order'
      btn.innerHTML = `<strong>${escapeHtml(head.orderNumber || head.externalId)}</strong><span>${head.orderedAt ? new Date(head.orderedAt).toLocaleDateString(locale || undefined) : ''}</span>`
      btn.addEventListener('click', () => {
        state.shipments = group
        state.order = group[0]
        show('SELECT_TYPE')
      })
      wrap.appendChild(btn)
    }
  }

  function renderOrders() { /* gefüllt durch loadMyOrders */ }

  // ── Sendungen (Shipments) ──────────────────────────────────
  function getShipments() {
    if (Array.isArray(state.shipments) && state.shipments.length) return state.shipments
    return state.order ? [state.order] : []
  }

  // "Sendung vom 15.05.2026" — Versanddatum, sonst Bestelldatum als Fallback.
  function shipmentLabel(sh) {
    const iso = sh && (sh.shippedAt || sh.orderedAt)
    if (!iso) return STR.select_items_shipment_unknown || 'Sendung'
    const dateStr = new Date(iso).toLocaleDateString(locale || undefined)
    return (STR.select_items_shipment_label || 'Sendung vom {date}').replace('{date}', dateStr)
  }

  // Erlaubt eine einzelne Sendung den gewählten Vorgangstyp? Hier (Viamundi)
  // nur fristbasiert — abgelaufene Frist sperrt den Typ.
  function shipmentAllowsType(sh, type) {
    if (!sh) return false
    const dl = sh.deadlines ? sh.deadlines[type] : null
    if (dl && !dl.allowed) return false
    return true
  }

  // ── SELECT_TYPE ────────────────────────────────────────────
  root.querySelectorAll('.rma-type').forEach((btn) => {
    btn.addEventListener('click', () => {
      // Bei <button disabled> würde der Klick zwar nicht ankommen — wir
      // prüfen trotzdem defensiv, falls JS den Disabled-State nicht setzen
      // konnte.
      if (btn.disabled) return
      state.type = btn.dataset.type
      // PACKAGE_LOST: keine Item-Auswahl noetig — gesamte Order betroffen,
      // landet direkt in MANUAL_REVIEW (DHL-Tracer).
      if (state.type === 'PACKAGE_LOST') {
        state.selectedItems = []
        return show('CONFIRM')
      }
      show('SELECT_ITEMS')
    })
  })

  // Markiert Typ-Buttons je nach Frist: abgelaufene Typen werden deaktiviert
  // und der Hint-Text wird durch eine Frist-Begründung ersetzt. Die Frist-
  // Daten kommen aus dem Order-Lookup (state.order.deadlines).
  function renderTypeButtons() {
    // Über alle Sendungen der Bestellung gerechnet: ein Typ ist wählbar, sobald
    // ihn MINDESTENS EINE Sendung erlaubt (fristbasiert). Die Frist-Sperre
    // greift dann im SELECT_ITEMS-Step pro Sendung. Bei nur einer Sendung
    // verhält sich das identisch zum bisherigen Verhalten.
    const shipments = getShipments()
    root.querySelectorAll('.rma-type').forEach((btn) => {
      const type = btn.dataset.type
      const hintEl = btn.querySelector('span')
      const originalHint = btn.dataset.originalHint != null
        ? btn.dataset.originalHint
        : (btn.dataset.originalHint = hintEl ? hintEl.innerHTML : '')
      const anyAllows = shipments.some((sh) => shipmentAllowsType(sh, type))
      if (anyAllows || shipments.length === 0) {
        btn.disabled = false
        btn.removeAttribute('aria-disabled')
        if (hintEl) hintEl.innerHTML = originalHint
        return
      }
      btn.disabled = true
      btn.setAttribute('aria-disabled', 'true')
      if (hintEl) {
        // Spätestes Fristdatum über alle Sendungen (am kulantesten).
        let latest = null
        for (const sh of shipments) {
          const dl = sh.deadlines ? sh.deadlines[type] : null
          if (dl && dl.deadlineAt) {
            const t = new Date(dl.deadlineAt).getTime()
            if (latest == null || t > latest) latest = t
          }
        }
        const dateStr = latest != null ? new Date(latest).toLocaleDateString(locale || undefined) : ''
        const tpl = type === 'WARRANTY'
          ? (STR.select_type_disabled_warranty || 'Garantiefrist abgelaufen am {date}.')
          : (STR.select_type_disabled_general || 'Frist abgelaufen am {date}.')
        hintEl.textContent = tpl.replace('{date}', dateStr)
      }
    })
  }

  // ── SELECT_ITEMS ───────────────────────────────────────────
  function renderItems() {
    const wrap = root.querySelector('[data-target="items"]')
    wrap.innerHTML = ''
    const shipments = getShipments()
    const multi = shipments.length > 1
    let renderedSelectable = 0
    for (const sh of shipments) {
      const typeBlockHint = shipmentAllowsType(sh, state.type) ? null : shipmentTypeBlockHint(sh, state.type)
      if (multi) {
        const header = document.createElement('div')
        header.className = 'rma-shipment-header'
        header.textContent = shipmentLabel(sh)
        wrap.appendChild(header)
      }
      const items = sh.items || []
      for (const item of items) {
        const isBundle = item.isBundle
        // Bei Garantie und Falschlieferung kann der Kunde einzelne Bundle-
        // Komponenten anmelden (defektes/falsches Einzelteil). Bei Rückgabe und
        // Umtausch geht nur das ganze Bundle.
        const splitBundle = isBundle && (state.type === 'WARRANTY' || state.type === 'WRONG_DELIVERY')
        if (splitBundle && (item.bundleParts || []).length > 0) {
          for (const part of item.bundleParts || []) {
            wrap.appendChild(itemRow({
              shipmentId: sh.orderId,
              orderLineId: item.orderLineId,
              sku: part.sku,
              title: `${item.title} — ${part.title}`,
              imageUrl: part.imageUrl,
              quantity: part.quantity,
              isBundleComponent: true,
              bundleResolutionId: part.bundleResolutionId,
              productId: part.productId,
              existingRma: item.existingRma,
              typeBlockHint,
            }))
            if (!item.existingRma && !typeBlockHint) renderedSelectable++
          }
        } else {
          wrap.appendChild(itemRow({
            shipmentId: sh.orderId,
            orderLineId: item.orderLineId,
            sku: item.sku,
            title: item.title,
            variantTitle: item.variantTitle,
            imageUrl: item.imageUrl,
            quantity: item.quantity,
            isBundleComponent: false,
            productId: item.productId,
            existingRma: item.existingRma,
            typeBlockHint,
          }))
          if (!item.existingRma && !typeBlockHint) renderedSelectable++
        }
      }
    }
    const noteEl = root.querySelector('[data-target="items-note"]')
    if (noteEl) noteEl.hidden = !(multi && renderedSelectable > 0)
    updateShipmentLock()
  }

  function rmaBlockHint(existingRma) {
    if (!existingRma) return ''
    // Statt nur 'Rückgabe angemeldet' zeigen wir den Vorgangstyp explizit.
    const labels = {
      RETURN: STR.item_blocked_return || 'Rücksendung bereits eingeleitet',
      EXCHANGE: STR.item_blocked_exchange || 'Umtausch bereits angemeldet',
      WARRANTY: STR.item_blocked_warranty || 'Garantiefall bereits angemeldet',
    }
    return labels[existingRma.type] || (STR.item_blocked_generic || 'Vorgang bereits angemeldet')
  }

  // Begründung, warum ein Artikel dieser Sendung beim gewählten Typ nicht
  // wählbar ist — hier (Viamundi) nur Fristablauf.
  function shipmentTypeBlockHint(sh, type) {
    const dl = sh.deadlines ? sh.deadlines[type] : null
    const dateStr = dl && dl.deadlineAt ? new Date(dl.deadlineAt).toLocaleDateString(locale || undefined) : ''
    const tpl = type === 'WARRANTY'
      ? (STR.select_type_disabled_warranty || 'Garantiefrist abgelaufen am {date}.')
      : (STR.select_type_disabled_general || 'Frist abgelaufen am {date}.')
    return tpl.replace('{date}', dateStr)
  }

  function itemRow({ shipmentId, orderLineId, sku, title, variantTitle, imageUrl, quantity, isBundleComponent, bundleResolutionId, productId, existingRma, typeBlockHint }) {
    // existingRma (laufender Case) hat Vorrang vor der Typ-Sperre der Sendung.
    const blockedHint = existingRma ? rmaBlockHint(existingRma) : (typeBlockHint || null)
    const isBlocked = !!blockedHint
    const tag = isBlocked ? 'div' : 'label'
    const div = document.createElement(tag)
    div.className = 'rma-item' + (isBlocked ? ' rma-item--blocked' : '')
    if (isBlocked) {
      // Kein Checkbox-Input, dafür ein erklärender Hinweis. Bei laufendem Case
      // zusätzlich die Case-Nummer.
      div.innerHTML = `
        ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="" class="rma-item__img">` : ''}
        <div class="rma-item__info">
          <strong>${escapeHtml(title)}</strong>
          ${variantTitle ? `<span class="rma-item__variant">${escapeHtml(variantTitle)}</span>` : ''}
          <span>${escapeHtml(sku)} · ${quantity}×</span>
          <span class="rma-item__blocked-hint">${escapeHtml(blockedHint)}${existingRma ? ` <span class="rma-item__blocked-case">(${escapeHtml(existingRma.caseNumber)})</span>` : ''}</span>
        </div>
      `
    } else {
      // Mengen-Stepper nur im Rückgabe-Flow und nur, wenn mehr als 1 Stück
      // bestellt wurde — damit ein Kunde, der z.B. 3 gleiche Artikel
      // zurückschickt, die Menge angeben kann (vorher implizit immer 1).
      // Default = bestellte Menge: "Artikel zurücksenden" meint meist alle
      // Stück; der Kunde sieht/bestätigt die Menge im Confirm-Step und kann
      // sie hier reduzieren.
      const showQty = state.type === 'RETURN' && quantity > 1
      const qtyLabel = escapeHtml(STR.qty_label || 'Menge')
      div.innerHTML = `
        <input type="checkbox" data-line="${orderLineId}" data-shipment="${escapeHtml(shipmentId || '')}" data-bundle-resolution="${bundleResolutionId || ''}" data-component="${isBundleComponent ? '1' : '0'}" data-product="${escapeHtml(productId || '')}" data-sku="${escapeHtml(sku)}" data-title="${escapeHtml(title)}" data-variant="${escapeHtml(variantTitle || '')}" data-image="${escapeHtml(imageUrl || '')}">
        ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="" class="rma-item__img">` : ''}
        <div class="rma-item__info">
          <strong>${escapeHtml(title)}</strong>
          ${variantTitle ? `<span class="rma-item__variant">${escapeHtml(variantTitle)}</span>` : ''}
          <span>${escapeHtml(sku)} · ${quantity}×</span>
        </div>
        ${showQty ? `
        <div class="rma-item__qty" data-qty-wrap>
          <span class="rma-item__qty-label">${qtyLabel}</span>
          <button type="button" class="rma-item__qty-btn" data-qty-dec aria-label="−" tabindex="-1">−</button>
          <input type="number" class="rma-item__qty-input" data-qty min="1" max="${quantity}" value="${quantity}" step="1" inputmode="numeric" aria-label="${qtyLabel}">
          <button type="button" class="rma-item__qty-btn" data-qty-inc aria-label="+" tabindex="-1">+</button>
        </div>` : ''}
      `
    }
    return div
  }

  // Eine Rücksendung darf nur Artikel aus EINER Sendung enthalten. Sobald in
  // einer Sendung etwas angehakt ist, werden die Checkboxen der anderen
  // Sendungen gesperrt. Bei nur einer Sendung passiert hier nichts.
  function updateShipmentLock() {
    const boxes = Array.from(root.querySelectorAll('[data-target="items"] input[type="checkbox"]'))
    let active = null
    for (const cb of boxes) {
      if (cb.checked) { active = cb.dataset.shipment || ''; break }
    }
    for (const cb of boxes) {
      const locked = active != null && (cb.dataset.shipment || '') !== active
      cb.disabled = locked
      const row = cb.closest('.rma-item')
      if (row) row.classList.toggle('rma-item--locked', locked)
    }
  }

  // Fehler verschwinden lassen, sobald der User wieder etwas anhakt; zugleich
  // die Ein-Sendung-Sperre nachziehen.
  root.querySelector('[data-target="items"]').addEventListener('change', () => {
    showError('items', '')
    updateShipmentLock()
  })

  // Mengen-Stepper: +/- anpassen, ohne die Checkbox des umschließenden
  // <label> zu togglen (preventDefault/stopPropagation). Ein Klick aktiviert
  // zugleich die Zeile, damit die Menge nicht ohne ausgewählten Artikel zählt.
  root.querySelector('[data-target="items"]').addEventListener('click', (e) => {
    const stepBtn = e.target.closest('[data-qty-dec], [data-qty-inc]')
    if (stepBtn) {
      e.preventDefault()
      e.stopPropagation()
      const row = stepBtn.closest('.rma-item')
      const input = row && row.querySelector('[data-qty]')
      if (!input) return
      const max = parseInt(input.max, 10) || 1
      const delta = stepBtn.hasAttribute('data-qty-inc') ? 1 : -1
      let v = (parseInt(input.value, 10) || 1) + delta
      v = Math.min(Math.max(1, v), max)
      input.value = String(v)
      const cb = row.querySelector('input[type="checkbox"]')
      if (cb && !cb.checked) cb.checked = true
      return
    }
    // Klick ins Zahlenfeld selbst darf die Zeilen-Checkbox nicht umschalten.
    if (e.target.closest('[data-qty]')) e.stopPropagation()
  })

  // Manuell eingetippte Menge beim Verlassen des Feldes auf [1, max] klemmen.
  root.querySelector('[data-target="items"]').addEventListener('change', (e) => {
    const input = e.target.closest('[data-qty]')
    if (!input) return
    const max = parseInt(input.max, 10) || 1
    let v = parseInt(input.value, 10)
    if (Number.isNaN(v) || v < 1) v = 1
    if (v > max) v = max
    input.value = String(v)
  })

  root.querySelector('[data-action="next-from-items"]').addEventListener('click', async (e) => {
    showError('items', '')
    const checked = Array.from(root.querySelectorAll('[data-target="items"] input[type="checkbox"]:checked'))
    if (checked.length === 0) {
      showError('items', STR.err_pick_item || 'Bitte wähle mindestens einen Artikel aus.')
      return
    }
    // Alle angehakten Artikel gehören derselben Sendung (die Ein-Sendung-Sperre
    // garantiert das). state.order auf genau diese Sendung setzen.
    const chosenShipmentId = checked[0].dataset.shipment || ''
    const chosenShipment = getShipments().find((s) => (s.orderId || '') === chosenShipmentId)
    if (chosenShipment) state.order = chosenShipment
    state.selectedItems = checked.map((cb) => {
      // Menge aus dem Stepper lesen (falls vorhanden) und defensiv auf
      // [1, bestellte Menge] begrenzen. Ohne Stepper (Einzelstück oder
      // Nicht-Rückgabe-Flow) bleibt es bei 1 wie bisher.
      const row = cb.closest('.rma-item')
      const qtyInput = row && row.querySelector('[data-qty]')
      let quantity = 1
      if (qtyInput) {
        const max = parseInt(qtyInput.max, 10) || 1
        quantity = Math.min(Math.max(1, parseInt(qtyInput.value, 10) || 1), max)
      }
      return {
        orderLineId: cb.dataset.line,
        sku: cb.dataset.sku,
        title: cb.dataset.title || cb.dataset.sku,
        variantTitle: cb.dataset.variant || '',
        imageUrl: cb.dataset.image || '',
        bundleResolutionId: cb.dataset.bundleResolution || null,
        isBundleComponent: cb.dataset.component === '1',
        productId: cb.dataset.product || null,
        quantity,
      }
    })
    // Neue Top-Level-Faelle: WRONG_DELIVERY zu Detail-Step (Text + Fotos),
    // MISSING_ITEM direkt zur Resolution-Wahl. Kein Shipping-Step — Backend
    // entscheidet selbst (auto-decide).
    if (state.type === 'WRONG_DELIVERY') return show('WRONG_DELIVERY_DETAILS')
    if (state.type === 'MISSING_ITEM') return show('RESOLUTION')
    // EXCHANGE fuehrt IMMER ueber EXCHANGE_DETAILS: dort waehlt der Kunde pro
    // Artikel, was passieren soll (andere Groesse / Gutschein / Geld zurueck).
    // Frueher wurde der Schritt uebersprungen, wenn kein Artikel alternative
    // Groessen hatte — dann wurde stillschweigend "Gutschein" gesetzt, obwohl
    // der Kunde vielleicht sein Geld zurueck wollte.
    if (state.type === 'EXCHANGE') return show('EXCHANGE_DETAILS')
    if (state.type === 'WARRANTY') return show('WARRANTY_PHOTO')
    return goNextFromItems()
  })

  function goNextFromItems() {
    if (state.channelKey === 'amazon') return show('AMAZON_CONTACT')
    // Shopify-Größentausch: Adress-Schritt anbieten (Adresse aus Order
    // ist vorausgewählt, Toggle für andere Lieferadresse).
    const hasSizeExchange = state.type === 'EXCHANGE'
      && Object.values(state.exchangeDetails).some((d) => d.exchangeMode === 'SAME_PRODUCT_DIFFERENT_VARIANT')
    if (hasSizeExchange) return show('SHOPIFY_EXCHANGE_ADDRESS')
    return loadShipping()
  }

  // ── EXCHANGE_DETAILS ───────────────────────────────────────
  async function renderExchange() {
    const wrap = root.querySelector('[data-target="exchange"]')
    wrap.innerHTML = ''
    setLoading(true)

    // Variants pro Item parallel laden (1 Roundtrip pro Item). Artikel ohne
    // Größen-Varianten (Nicht-Shirts) bekommen einfach keine Größen-Optionen,
    // ihnen bleiben Gutschein und Geld zurück.
    const variantsPerItem = await Promise.all(state.selectedItems.map((it) =>
      post('/apps/rma/api/shirt-variants', { sku: it.sku }).then((r) => ({
        item: it,
        variants: (r.ok && r.isShirt && r.variants?.length > 0) ? r.variants.filter((v) => !v.isCurrent) : [],
      }))
    ))
    setLoading(false)

    for (const { item: it, variants } of variantsPerItem) {
      const det = state.exchangeDetails[it.orderLineId] || {}
      const div = document.createElement('div')
      div.className = 'rma-exchange-row'
      div.innerHTML = `<strong>${escapeHtml(it.title || it.sku)}</strong>${it.variantTitle ? ` <span class="rma-item__variant">${escapeHtml(it.variantTitle)}</span>` : ''}`
      const select = document.createElement('select')
      select.dataset.line = it.orderLineId
      // Bewusst keine Vorauswahl: der Kunde soll pro Artikel aktiv entscheiden.
      // Eine stille Vorbelegung (früher: Gutschein) verwandelt sonst einen
      // Erstattungswunsch in einen Gutschein, den niemand wollte.
      const optPlaceholder = document.createElement('option')
      optPlaceholder.value = ''
      optPlaceholder.textContent = STR.exchange_choose || 'Bitte wählen …'
      select.appendChild(optPlaceholder)
      for (const v of variants) {
        const opt = document.createElement('option')
        opt.value = `SIZE:${v.sku}`
        opt.textContent = (STR.exchange_target_size || 'Größe') + ': ' + v.size
        select.appendChild(opt)
      }
      const optV = document.createElement('option')
      optV.value = 'VOUCHER'
      optV.textContent = STR.exchange_voucher || 'Gutschein'
      select.appendChild(optV)
      const optR = document.createElement('option')
      optR.value = 'REFUND'
      optR.textContent = STR.exchange_refund || 'Geld zurück'
      select.appendChild(optR)
      // Bestehende Wahl wiederherstellen (Zurück-Navigation).
      select.value = det.exchangeMode === 'SAME_PRODUCT_DIFFERENT_VARIANT' && det.exchangeTargetSku
        ? `SIZE:${det.exchangeTargetSku}`
        : (det.exchangeMode || '')
      div.appendChild(select)
      wrap.appendChild(div)
    }
  }

  // Auswahl geändert → Fehlermeldung verschwinden lassen.
  root.querySelector('[data-target="exchange"]').addEventListener('change', () => showError('exchange', ''))

  root.querySelector('[data-action="next-from-exchange"]').addEventListener('click', () => {
    const selects = Array.from(root.querySelectorAll('[data-target="exchange"] select'))
    // Jeder Artikel braucht eine Entscheidung — sonst käme er mit
    // exchangeMode=null beim Backend an und würde dort (exchange_mode_missing)
    // hart abgelehnt.
    if (selects.some((s) => !s.value)) {
      showError('exchange', STR.err_exchange_pick || 'Bitte wähle für jeden Artikel aus, was passieren soll.')
      return
    }
    showError('exchange', '')
    state.exchangeDetails = {}
    selects.forEach((s) => {
      const line = s.dataset.line
      const v = s.value
      if (v === 'VOUCHER') {
        state.exchangeDetails[line] = { exchangeMode: 'VOUCHER' }
      } else if (v === 'REFUND') {
        state.exchangeDetails[line] = { exchangeMode: 'REFUND' }
      } else if (v.startsWith('SIZE:')) {
        state.exchangeDetails[line] = { exchangeMode: 'SAME_PRODUCT_DIFFERENT_VARIANT', exchangeTargetSku: v.slice(5) }
      }
    })
    return goNextFromItems()
  })

  // ── WARRANTY_PHOTO ─────────────────────────────────────────
  function renderPhotos() {
    const wrap = root.querySelector('[data-target="photos"]')
    wrap.innerHTML = ''
    for (const it of state.selectedItems) {
      const div = document.createElement('div')
      div.className = 'rma-photo-row'
      div.dataset.line = it.orderLineId
      const inputId = `rma-photo-input-${it.orderLineId}`
      const reasonId = `rma-reason-input-${it.orderLineId}`
      div.innerHTML = `
        <div class="rma-photo-row__head">
          ${it.imageUrl ? `<img src="${escapeHtml(it.imageUrl)}" alt="" class="rma-item__img">` : ''}
          <div class="rma-item__info">
            <strong>${escapeHtml(it.title)}</strong>
            ${it.variantTitle ? `<span class="rma-item__variant">${escapeHtml(it.variantTitle)}</span>` : ''}
            <span>${escapeHtml(it.sku)}</span>
          </div>
        </div>
        <div class="rma-photo-row__reason">
          <label for="${reasonId}" class="rma-photo-reason__label">${escapeHtml(STR.warranty_reason_label || 'Was ist defekt?')}</label>
          <textarea id="${reasonId}" data-reason-line="${it.orderLineId}" rows="2" class="rma-photo-reason__input" placeholder="${escapeHtml(STR.warranty_reason_placeholder || 'z.B. Akku lädt nicht mehr, LED kaputt …')}"></textarea>
        </div>
        <div class="rma-photo-row__actions">
          <input type="file" accept="image/*" multiple id="${inputId}" data-line="${it.orderLineId}" class="rma-photo-input">
          <label for="${inputId}" class="rma-photo-pick">${escapeHtml(STR.photo_pick || 'Foto hinzufügen')}</label>
          <span class="rma-photo-status" data-photo-status="${it.orderLineId}"></span>
        </div>
        <div class="rma-photo-thumbs" data-photo-thumbs="${it.orderLineId}"></div>
      `
      wrap.appendChild(div)
      // Bestehende Werte wiederherstellen (Back-Navi)
      const reasonInput = div.querySelector(`#${reasonId}`)
      if (reasonInput && state.warrantyReasons[it.orderLineId]) {
        reasonInput.value = state.warrantyReasons[it.orderLineId]
      }
      for (const url of state.photos[it.orderLineId] || []) appendThumb(it.orderLineId, url)
    }
    wrap.querySelectorAll('textarea[data-reason-line]').forEach((ta) => {
      ta.addEventListener('input', () => {
        state.warrantyReasons[ta.dataset.reasonLine] = ta.value
      })
    })
    wrap.querySelectorAll('input.rma-photo-input').forEach((inp) => {
      inp.addEventListener('change', async (e) => {
        const line = inp.dataset.line
        const files = Array.from(e.target.files || [])
        if (files.length === 0) return
        for (const f of files) await uploadPhoto(line, f)
        inp.value = ''
      })
    })
  }

  function appendThumb(line, photoUrl) {
    const grid = root.querySelector(`[data-photo-thumbs="${line}"]`)
    if (!grid) return
    const thumb = document.createElement('div')
    thumb.className = 'rma-photo-thumb'
    thumb.innerHTML = `
      <img src="${escapeHtml(photoUrl)}" alt="">
      <button type="button" class="rma-photo-remove" aria-label="${escapeHtml(STR.photo_remove_label || 'Entfernen')}">×</button>
    `
    thumb.querySelector('.rma-photo-remove').addEventListener('click', () => {
      const list = state.photos[line] || []
      const idx = list.indexOf(photoUrl)
      if (idx >= 0) list.splice(idx, 1)
      thumb.remove()
    })
    grid.appendChild(thumb)
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result || ''
        const comma = result.indexOf(',')
        resolve(comma >= 0 ? result.slice(comma + 1) : result)
      }
      reader.onerror = () => reject(reader.error || new Error('read failed'))
      reader.readAsDataURL(file)
    })
  }

  async function uploadPhoto(line, file) {
    const status = root.querySelector(`[data-photo-status="${line}"]`)
    if (status) status.textContent = '⏳'
    try {
      const dataBase64 = await fileToBase64(file)
      // JSON statt multipart — der Shopify-App-Proxy leitet kein
      // multipart/form-data durch (gibt 503). XHR statt fetch, weil ein
      // Storefront-Monitoring window.fetch wrappt und Responses verformt.
      const json = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('POST', url('/apps/rma/api/upload'), true)
        xhr.setRequestHeader('Content-Type', 'application/json')
        xhr.responseType = 'json'
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response)
          else reject(new Error('http ' + xhr.status + (xhr.response?.error ? ' ' + xhr.response.error : '')))
        }
        xhr.onerror = () => reject(new Error('network error'))
        xhr.send(JSON.stringify({ name: file.name, type: file.type, line, dataBase64 }))
      })
      if (!json?.ok || !json.url) throw new Error(json?.error || 'no url')
      if (!state.photos[line]) state.photos[line] = []
      state.photos[line].push(json.url)
      appendThumb(line, json.url)
      if (status) status.textContent = ''
    } catch (err) {
      console.error('[RMA] upload failed', err)
      if (status) status.textContent = '✗'
    }
  }

  root.querySelector('[data-action="next-from-photos"]').addEventListener('click', () => goNextFromItems())

  // ── WRONG_DELIVERY_DETAILS ────────────────────────────────
  // Freitext (was war stattdessen drin?) + optionale Fotos auf Case-Level.
  // Persist + Restore bei Back-Nav, analog zur Warranty-Photo-Logik.
  function renderWrongDeliveryDetails() {
    const ta = root.querySelector('[data-target="wrong-delivery-text"]')
    if (ta) ta.value = state.wrongDeliveryDescription || ''
    // Bestehende Thumbs neu zeichnen (z.B. nach Back-Nav).
    const grid = root.querySelector('[data-target="case-photo-thumbs"]')
    if (grid) {
      grid.innerHTML = ''
      for (const url of state.caseLevelPhotos || []) appendCasePhotoThumb(url)
    }
  }

  function appendCasePhotoThumb(photoUrl) {
    const grid = root.querySelector('[data-target="case-photo-thumbs"]')
    if (!grid) return
    const thumb = document.createElement('div')
    thumb.className = 'rma-photo-thumb'
    thumb.innerHTML = `
      <img src="${escapeHtml(photoUrl)}" alt="">
      <button type="button" class="rma-photo-remove" aria-label="${escapeHtml(STR.photo_remove_label || 'Entfernen')}">×</button>
    `
    thumb.querySelector('.rma-photo-remove').addEventListener('click', () => {
      const idx = state.caseLevelPhotos.indexOf(photoUrl)
      if (idx >= 0) state.caseLevelPhotos.splice(idx, 1)
      thumb.remove()
      persistState()
    })
    grid.appendChild(thumb)
  }

  async function uploadCasePhoto(file) {
    const status = root.querySelector('[data-target="case-photo-status"]')
    if (status) status.textContent = '⏳'
    try {
      const dataBase64 = await fileToBase64(file)
      const json = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('POST', url('/apps/rma/api/upload'), true)
        xhr.setRequestHeader('Content-Type', 'application/json')
        xhr.responseType = 'json'
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response)
          else reject(new Error('http ' + xhr.status + (xhr.response?.error ? ' ' + xhr.response.error : '')))
        }
        xhr.onerror = () => reject(new Error('network error'))
        // Backend erwartet das line-Feld — fuer Case-Level senden wir
        // einen Sentinel-Wert, das WMS legt das Foto trotzdem ab.
        xhr.send(JSON.stringify({ name: file.name, type: file.type, line: 'case', dataBase64 }))
      })
      if (!json?.ok || !json.url) throw new Error(json?.error || 'no url')
      state.caseLevelPhotos.push(json.url)
      appendCasePhotoThumb(json.url)
      persistState()
      if (status) status.textContent = ''
    } catch (err) {
      console.error('[RMA] case upload failed', err)
      if (status) status.textContent = '✗'
    }
  }

  const caseInput = root.querySelector('[data-target="case-photo-input"]')
  if (caseInput) {
    caseInput.addEventListener('change', async (e) => {
      const files = Array.from(e.target.files || [])
      if (files.length === 0) return
      for (const f of files) await uploadCasePhoto(f)
      e.target.value = ''
    })
  }

  root.querySelector('[data-action="next-from-wrong-delivery"]').addEventListener('click', () => {
    showError('wrong-delivery', '')
    const ta = root.querySelector('[data-target="wrong-delivery-text"]')
    state.wrongDeliveryDescription = (ta?.value || '').trim()
    persistState()
    show('CONFIRM')
  })

  // ── RESOLUTION (Ersatz oder Refund) ────────────────────────
  function renderResolution() {
    const radios = root.querySelectorAll('input[name="rma-resolution"]')
    radios.forEach((r) => { r.checked = (state.resolution === r.value) })
  }

  root.querySelector('[data-action="next-from-resolution"]').addEventListener('click', () => {
    showError('resolution', '')
    const sel = root.querySelector('input[name="rma-resolution"]:checked')
    if (!sel) {
      showError('resolution', STR.err_resolution_required || 'Bitte wähle Ersatz oder Geld zurück.')
      return
    }
    state.resolution = sel.value
    persistState()
    show('CONFIRM')
  })

  // ── SHOPIFY_EXCHANGE_ADDRESS ──────────────────────────────
  function renderShopifyExchangeAddress() {
    const addr = state.order?.customer?.address || null
    const preview = root.querySelector('[data-target="shopify-address-preview"]')
    if (preview) {
      if (addr) {
        const lines = [
          [addr.firstName, addr.lastName].filter(Boolean).join(' '),
          addr.company,
          addr.street,
          addr.address2,
          [addr.zip, addr.city].filter(Boolean).join(' '),
          addr.country,
        ].filter(Boolean).map(escapeHtml).join('<br>')
        preview.innerHTML = `<address>${lines}</address>`
      } else {
        preview.textContent = ''
      }
    }
    // Restore Override-State (z.B. nach Back-Navigation oder Reload)
    const toggle = root.querySelector('[data-target="shopify-address-override-toggle"]')
    const form = root.querySelector('[data-form="shopify-address"]')
    if (state.shopifyExchangeAddress) {
      if (toggle) toggle.checked = true
      if (form) {
        form.hidden = false
        form.querySelector('[name="street"]').value = state.shopifyExchangeAddress.street ?? ''
        form.querySelector('[name="zip"]').value = state.shopifyExchangeAddress.zip ?? ''
        form.querySelector('[name="city"]').value = state.shopifyExchangeAddress.city ?? ''
        form.querySelector('[name="country"]').value = state.shopifyExchangeAddress.country ?? ''
      }
    } else {
      if (toggle) toggle.checked = false
      if (form) form.hidden = true
    }
  }

  root.querySelector('[data-target="shopify-address-override-toggle"]').addEventListener('change', (e) => {
    const form = root.querySelector('[data-form="shopify-address"]')
    if (!form) return
    if (e.target.checked) {
      form.hidden = false
      // Vorbefüllen mit Order-Adresse, damit der User nur die abweichenden
      // Felder ändern muss.
      const addr = state.order?.customer?.address || {}
      if (!form.querySelector('[name="street"]').value) form.querySelector('[name="street"]').value = addr.street ?? ''
      if (!form.querySelector('[name="zip"]').value) form.querySelector('[name="zip"]').value = addr.zip ?? ''
      if (!form.querySelector('[name="city"]').value) form.querySelector('[name="city"]').value = addr.city ?? ''
      if (!form.querySelector('[name="country"]').value) form.querySelector('[name="country"]').value = addr.country ?? ''
    } else {
      form.hidden = true
      state.shopifyExchangeAddress = null
    }
  })

  root.querySelector('[data-action="next-from-shopify-address"]').addEventListener('click', () => {
    showError('shopify-address', '')
    const toggle = root.querySelector('[data-target="shopify-address-override-toggle"]')
    if (toggle?.checked) {
      const form = root.querySelector('[data-form="shopify-address"]')
      const street = form.querySelector('[name="street"]').value.trim()
      const zip = form.querySelector('[name="zip"]').value.trim()
      const city = form.querySelector('[name="city"]').value.trim()
      const country = form.querySelector('[name="country"]').value.trim().toUpperCase()
      if (!street || !zip || !city || !country) {
        showError('shopify-address', STR.err_shopify_address || 'Bitte fülle alle Adressfelder aus.')
        return
      }
      state.shopifyExchangeAddress = { street, zip, city, country }
    } else {
      state.shopifyExchangeAddress = null
    }
    return loadShipping()
  })

  // ── AMAZON_CONTACT ─────────────────────────────────────────
  function renderAmazonContact() {
    const needsAddress = state.type === 'EXCHANGE' && Object.values(state.exchangeDetails).some((d) => d.exchangeMode === 'SAME_PRODUCT_DIFFERENT_VARIANT')
    const fs = root.querySelector('[data-target="amazon-address"]')
    if (fs) fs.hidden = !needsAddress
  }

  root.querySelector('[data-form="amazon-contact"]').addEventListener('submit', async (e) => {
    e.preventDefault()
    showError('amazon-contact', '')
    const fd = new FormData(e.target)
    const email = (fd.get('email') || '').toString().trim()
    if (!email) {
      showError('amazon-contact', STR.err_amazon_email || 'Bitte gib eine E-Mail-Adresse an, unter der wir dich erreichen können.')
      return
    }
    const address = {
      street: (fd.get('street') || '').toString().trim(),
      zip: (fd.get('zip') || '').toString().trim(),
      city: (fd.get('city') || '').toString().trim(),
      country: (fd.get('country') || '').toString().trim().toUpperCase(),
    }
    const needsAddress = state.type === 'EXCHANGE' && Object.values(state.exchangeDetails).some((d) => d.exchangeMode === 'SAME_PRODUCT_DIFFERENT_VARIANT')
    if (needsAddress && (!address.street || !address.zip || !address.city || !address.country)) {
      showError('amazon-contact', STR.err_amazon_address || 'Für den Umtausch brauchen wir eine vollständige Lieferadresse.')
      return
    }
    state.amazonContact = {
      email,
      name: (fd.get('name') || '').toString().trim(),
      address,
    }
    return loadShipping()
  })

  // ── SHIPPING_OPTION ────────────────────────────────────────
  async function loadShipping() {
    show('SHIPPING_OPTION')
    setLoading(true)
    const country = state.channelKey === 'amazon'
      ? (state.amazonContact?.address?.country || state.amazonContact?.country || '')
      : (state.order?.customer?.country || state.order?.customer?.address?.country || '')
    const items = state.selectedItems.map((it) => {
      const productId = state.order?.items.find((i) => i.orderLineId === it.orderLineId)?.productId
      const photoCount = (state.photos[it.orderLineId] || []).length
      return {
        orderLineId: it.orderLineId,
        productId,
        quantity: it.quantity,
        hasPhoto: photoCount > 0,
      }
    })
    const r = await post('/apps/rma/api/shipping-options', { type: state.type, country, items })
    setLoading(false)
    if (!r.ok) {
      const wrap = root.querySelector('[data-target="shipping"]')
      wrap.textContent = STR.error_text || 'Fehler'
      return
    }
    state.autoRefund = !!r.autoRefund?.yes
    // Ausland: Abgabe beim Postpartner des Landes, nicht bei DHL (Satz kommt vom Server).
    state.dropOffHint = r.dropOffHint || ''
    renderShippingList(r.options || [])
  }

  function renderShippingList(options) {
    const wrap = root.querySelector('[data-target="shipping"]')
    wrap.innerHTML = ''
    if (state.autoRefund) {
      const div = document.createElement('p')
      div.textContent = STR.shipping_auto_refund_hint || 'Wir erstatten dir den Betrag direkt — keine Rücksendung nötig.'
      wrap.appendChild(div)
      state.shippingOption = 'auto-refund'
      return
    }
    for (let i = 0; i < options.length; i++) {
      const opt = options[i]
      const labelText = labelForOption(opt)
      const lbl = document.createElement('label')
      lbl.className = 'rma-shipping-option'
      // Erste Option vorauswählen — sonst klickt "Weiter" ins Leere.
      const checked = i === 0 ? 'checked' : ''
      lbl.innerHTML = `
        <input type="radio" name="shipping" value="${opt.kind}" data-fee="${opt.feeCents}" ${checked}>
        <span>${escapeHtml(labelText)}${opt.feeCents > 0 ? ` (${(opt.feeCents / 100).toFixed(2)} €)` : ''}</span>
      `
      if (opt.hintKey) {
        const hint = document.createElement('em')
        hint.textContent = STR.shipping_fee_deducted || 'Gebühr wird vom Erstattungsbetrag abgezogen'
        lbl.appendChild(hint)
      }
      if (opt.kind === 'self-return') {
        const hint = document.createElement('em')
        hint.className = 'rma-shipping-option__hint'
        hint.textContent = STR.shipping_self_hint || 'Versandkosten trägst du selbst.'
        lbl.appendChild(hint)
      }
      if (opt.kind === 'dhl-return-free' || opt.kind === 'dhl-return-paid') {
        const hint = document.createElement('em')
        hint.className = 'rma-shipping-option__hint'
        hint.textContent = state.dropOffHint || STR.shipping_dhl_hint || 'Wir erstellen ein DHL-Retourelabel. Du kannst es selbst ausdrucken oder den QR-Code in einer DHL-Filiale scannen lassen — Abgabe in der Filiale oder Mitgabe beim DHL-Fahrer.'
        lbl.appendChild(hint)
      }
      wrap.appendChild(lbl)
    }
    // State sofort auf die vorausgewählte Option setzen, falls der User
    // direkt auf Weiter klickt ohne ein Radio anzufassen.
    if (options.length > 0) {
      state.shippingOption = options[0].kind
      state.shippingFeeCents = options[0].feeCents || 0
    }
  }

  function shippingHintFor(kind) {
    if (kind === 'self-return') {
      return STR.shipping_self_hint || 'Versandkosten trägst du selbst.'
    }
    if (kind === 'dhl-return-free' || kind === 'dhl-return-paid') {
      return state.dropOffHint || STR.shipping_dhl_hint || 'Wir erstellen ein DHL-Retourelabel. Du kannst es selbst ausdrucken oder den QR-Code in einer DHL-Filiale scannen lassen — Abgabe in der Filiale oder Mitgabe beim DHL-Fahrer.'
    }
    return ''
  }

  function labelForOption(opt) {
    switch (opt.kind) {
      case 'dhl-return-free': return STR.shipping_dhl_free || 'DHL kostenlos'
      case 'dhl-return-paid': return STR.shipping_dhl_paid || 'DHL kostenpflichtig'
      case 'self-return': return STR.shipping_self || 'Selbst zurück'
      case 'no-shipping-manual-review': return STR.shipping_manual || 'Wir melden uns'
      default: return opt.kind
    }
  }

  function renderShipping() { /* gefüllt durch loadShipping */ }

  root.querySelector('[data-target="shipping"]').addEventListener('change', () => {
    showError('shipping', '')
  })

  root.querySelector('[data-action="next-from-shipping"]').addEventListener('click', () => {
    showError('shipping', '')
    const sel = root.querySelector('[data-target="shipping"] input[name="shipping"]:checked')
    if (sel) {
      state.shippingOption = sel.value
      state.shippingFeeCents = parseInt(sel.dataset.fee, 10) || 0
    }
    if (!state.shippingOption && !state.autoRefund) {
      showError('shipping', STR.shipping_pick_required || 'Bitte eine Versandoption wählen.')
      return
    }
    show('CONFIRM')
  })

  // ── CONFIRM ────────────────────────────────────────────────
  function renderSummary() {
    const wrap = root.querySelector('[data-target="summary"]')
    const html = []
    const typeLabel = state.type === 'RETURN' ? (STR.type_return || 'Rückgabe')
      : state.type === 'EXCHANGE' ? (STR.type_exchange || 'Umtausch')
      : state.type === 'WARRANTY' ? (STR.type_warranty || 'Garantie')
      : state.type === 'WRONG_DELIVERY' ? (STR.type_wrong_delivery || 'Falsche Ware geliefert')
      : state.type === 'MISSING_ITEM' ? (STR.type_missing_item || 'Artikel fehlt')
      : state.type === 'PACKAGE_LOST' ? (STR.type_package_lost || 'Paket nicht angekommen')
      : state.type

    // Vorgang + Versand
    html.push(`<dl class="rma-summary__meta">`)
    html.push(`<dt>${escapeHtml(STR.summary_type || 'Vorgang')}</dt><dd>${escapeHtml(typeLabel)}</dd>`)
    if (isNewFlowType(state.type)) {
      // Neue Faelle: Backend entscheidet selbst ueber Label/keine-Retoure
      // und ggf. MANUAL_REVIEW. Wir zeigen Resolution und sparen uns die
      // Versand-Zeile.
      if (state.resolution) {
        const resLabel = state.resolution === 'REPLACEMENT'
          ? (STR.resolution_replacement || 'Ersatz schicken')
          : (STR.resolution_refund || 'Geld zurück')
        html.push(`<dt>${escapeHtml(STR.summary_resolution || 'Lösung')}</dt><dd>${escapeHtml(resLabel)}</dd>`)
      }
    } else if (state.autoRefund) {
      html.push(`<dt>${escapeHtml(STR.summary_shipping || 'Versand')}</dt><dd>${escapeHtml(STR.summary_auto_refund || 'Sofort-Erstattung — keine Rücksendung nötig')}</dd>`)
    } else {
      const shippingLabel = labelForOption({ kind: state.shippingOption })
      const fee = state.shippingFeeCents ? ` (${(state.shippingFeeCents / 100).toFixed(2)} €)` : ''
      const hint = shippingHintFor(state.shippingOption)
      const hintHtml = hint ? `<br><small class="rma-summary__hint">${escapeHtml(hint)}</small>` : ''
      html.push(`<dt>${escapeHtml(STR.summary_shipping || 'Versand')}</dt><dd>${escapeHtml(shippingLabel)}${fee}${hintHtml}</dd>`)
    }

    // Kontakt — bei Amazon aus dem Wizard, sonst aus der Order
    const contactEmail = state.channelKey === 'amazon'
      ? state.amazonContact?.email
      : state.order?.customer?.email
    if (contactEmail) {
      html.push(`<dt>${escapeHtml(STR.summary_email || 'E-Mail')}</dt><dd>${escapeHtml(contactEmail)}</dd>`)
    }
    html.push(`</dl>`)

    // Falsche-Ware: Freitext + Case-Level-Fotos
    if (state.type === 'WRONG_DELIVERY') {
      const desc = (state.wrongDeliveryDescription || '').trim()
      if (desc) {
        html.push(`<div class="rma-summary__wrong-delivery">`)
        html.push(`<p><strong>${escapeHtml(STR.wrong_delivery_text_label || 'Was war stattdessen im Paket?')}</strong></p>`)
        html.push(`<p class="rma-summary__reason">${escapeHtml(desc)}</p>`)
        html.push(`</div>`)
      }
      if ((state.caseLevelPhotos || []).length > 0) {
        html.push(`<div class="rma-summary__photos">`)
        for (const u of state.caseLevelPhotos) {
          html.push(`<img src="${escapeHtml(u)}" alt="" class="rma-summary__photo">`)
        }
        html.push(`</div>`)
      }
    }

    // Items
    html.push(`<div class="rma-summary__items">`)
    for (const it of state.selectedItems) {
      const o = state.order?.items.find((i) => i.orderLineId === it.orderLineId)
      const title = it.title || o?.title || it.sku
      const variantTitle = it.variantTitle || o?.variantTitle || ''
      const imageUrl = it.imageUrl || o?.imageUrl || ''
      const reason = (state.warrantyReasons[it.orderLineId] || '').trim()
      const photos = state.photos[it.orderLineId] || []
      const det = state.exchangeDetails[it.orderLineId] || {}

      html.push(`<div class="rma-summary__item">`)
      html.push(`<div class="rma-summary__item-head">`)
      if (imageUrl) html.push(`<img src="${escapeHtml(imageUrl)}" alt="" class="rma-item__img">`)
      html.push(`<div class="rma-item__info">`)
      html.push(`<strong>${escapeHtml(title)}</strong>`)
      if (variantTitle) html.push(`<span class="rma-item__variant">${escapeHtml(variantTitle)}</span>`)
      html.push(`<span>${escapeHtml(it.sku)} · ${it.quantity}×</span>`)
      html.push(`</div>`)
      html.push(`</div>`)

      // Tausch-Details
      if (det.exchangeMode === 'VOUCHER') {
        html.push(`<p class="rma-summary__row"><strong>${escapeHtml(STR.summary_exchange || 'Tausch')}:</strong> ${escapeHtml(STR.exchange_voucher || 'Gutschein')}</p>`)
      } else if (det.exchangeMode === 'REFUND') {
        html.push(`<p class="rma-summary__row"><strong>${escapeHtml(STR.summary_exchange || 'Tausch')}:</strong> ${escapeHtml(STR.exchange_refund || 'Geld zurück')}</p>`)
      } else if (det.exchangeMode === 'SAME_PRODUCT_DIFFERENT_VARIANT' && det.exchangeTargetSku) {
        html.push(`<p class="rma-summary__row"><strong>${escapeHtml(STR.summary_exchange || 'Tausch')}:</strong> ${escapeHtml(STR.exchange_target_size || 'Größe')} → ${escapeHtml(det.exchangeTargetSku)}</p>`)
      }

      // Defekt-Beschreibung
      if (reason) {
        html.push(`<p class="rma-summary__row"><strong>${escapeHtml(STR.warranty_reason_label || 'Was ist defekt?')}</strong></p>`)
        html.push(`<p class="rma-summary__reason">${escapeHtml(reason)}</p>`)
      }

      // Fotos
      if (photos.length > 0) {
        html.push(`<div class="rma-summary__photos">`)
        for (const url of photos) {
          html.push(`<img src="${escapeHtml(url)}" alt="" class="rma-summary__photo">`)
        }
        html.push(`</div>`)
      }

      html.push(`</div>`)
    }
    html.push(`</div>`)

    // „Wie es weitergeht" — vorab das Versprechen aus dem RESULT-Schritt
    // zeigen, damit der Kunde vor dem Anmelden weiß, was als Nächstes passiert.
    const promise = nextStepsPromise()
    if (promise) {
      html.push(`<div class="rma-summary__promise">`)
      html.push(`<p><strong>${escapeHtml(STR.summary_what_next || 'Wie es weitergeht:')}</strong></p>`)
      html.push(`<p>${escapeHtml(promise)}</p>`)
      html.push(`</div>`)
    }

    wrap.innerHTML = html.join('')
  }

  function nextStepsPromise() {
    // Neue Top-Level-Faelle: was im Result steht, weiss erst der Server
    // (Label vs. behalten, Auto-Ersatz vs. MANUAL_REVIEW). Im CONFIRM
    // geben wir dem Kunden eine ehrliche, weiche Aussage.
    if (state.type === 'PACKAGE_LOST') {
      return STR.result_package_lost || 'Wir kümmern uns: zuerst fragen wir bei DHL nach, was mit deinem Paket passiert ist. Sobald wir Bescheid wissen, melden wir uns bei dir.'
    }
    if (isNewFlowType(state.type)) {
      if (state.type === 'WRONG_DELIVERY') {
        // Falschlieferung wird manuell geprueft — keine Outcome-Zusage, nur
        // der Hinweis, dass wir uns zeitnah melden.
        return STR.result_manual_review_new_flow || STR.result_manual_review || 'Wir prüfen deinen Fall und melden uns zeitnah bei dir.'
      }
      if (state.resolution === 'REPLACEMENT') {
        return STR.result_replacement_missing || 'Wir prüfen deinen Fall und schicken dir die fehlende oder falsche Ware in Kürze nach.'
      }
      return STR.result_refund_missing || 'Wir prüfen deinen Fall und erstatten dir den Kaufbetrag in Kürze.'
    }
    const isAutoRefund = state.autoRefund
    if (isAutoRefund) {
      return STR.result_auto_refund || 'Wir haben dir den Kaufbetrag bereits auf deine ursprüngliche Zahlungsmethode erstattet — eine Bestätigung kommt per E-Mail. Du musst nichts zurückschicken. Vielen Dank!'
    }
    const isManual = state.shippingOption === 'no-shipping-manual-review'
    if (isManual) {
      return STR.result_manual_review || 'Wir prüfen deinen Fall manuell und melden uns innerhalb weniger Werktage per E-Mail bei dir.'
    }
    const hasLabel = state.shippingOption === 'dhl-return-free' || state.shippingOption === 'dhl-return-paid'

    if (state.type === 'RETURN') {
      return hasLabel
        ? (STR.result_return_promise_first_scan || 'Sobald die Rücksendung bei uns angekommen und geprüft ist, erstatten wir dir den Kaufbetrag — meist innerhalb weniger Tage. Du bekommst eine Bestätigung per E-Mail.')
        : (STR.result_return_promise_arrival || 'Sobald die Rücksendung bei uns angekommen und geprüft ist, erstatten wir dir den Kaufbetrag. Du bekommst eine Bestätigung per E-Mail.')
    }
    if (state.type === 'EXCHANGE') {
      // Pro-Artikel-Modus: es koennen mehrere Ausgaenge gleichzeitig zutreffen
      // (z.B. eine Groesse tauschen und zwei Artikel erstatten). Deshalb alle
      // zutreffenden Zusagen sammeln statt nur die erste zurueckzugeben.
      return exchangePromises(hasLabel).join(' ')
    }
    if (state.type === 'WARRANTY') {
      return STR.result_warranty_arrival || 'Nach Empfang und Prüfung bearbeiten wir den Garantiefall — du erhältst Ersatz, Reparatur oder Erstattung. Wir melden uns per E-Mail.'
    }
    return ''
  }

  // Zusagen fuer einen Umtausch-Vorgang — eine pro tatsaechlich gewaehltem
  // Per-Artikel-Modus, in der Reihenfolge Groessentausch → Gutschein →
  // Erstattung.
  function exchangePromises(hasLabel) {
    const modes = Object.values(state.exchangeDetails).map((d) => d.exchangeMode)
    const out = []
    if (modes.includes('SAME_PRODUCT_DIFFERENT_VARIANT')) {
      out.push(hasLabel
        ? (STR.result_size_exchange_first_scan || 'Sobald die Rücksendung bei uns angekommen ist, bereiten wir den Tausch in deine Wunschgröße vor und schicken dir die neue Ware mit Tracking per E-Mail.')
        : (STR.result_size_exchange_arrival || 'Sobald die Rücksendung bei uns angekommen ist, schicken wir dir den Tausch in deine Wunschgröße — Tracking per E-Mail.'))
    }
    if (modes.includes('VOUCHER')) {
      out.push(hasLabel
        ? (STR.result_voucher_promise_first_scan || 'Sobald die Rücksendung bei uns angekommen ist, schicken wir dir den Gutschein per E-Mail. Falls die Mail nicht ankommt, prüfe bitte auch deinen Spam-Ordner.')
        : (STR.result_voucher_promise_arrival || 'Sobald die Rücksendung bei uns angekommen ist, schicken wir dir den Gutschein per E-Mail. Falls die Mail nicht ankommt, prüfe bitte auch deinen Spam-Ordner.'))
    }
    if (modes.includes('REFUND')) {
      out.push(hasLabel
        ? (STR.result_item_refund_first_scan || 'Für die Artikel, bei denen du „Geld zurück“ gewählt hast, erstatten wir den Kaufbetrag, sobald die Rücksendung bei uns angekommen und geprüft ist — Bestätigung per E-Mail.')
        : (STR.result_item_refund_arrival || 'Für die Artikel, bei denen du „Geld zurück“ gewählt hast, erstatten wir den Kaufbetrag nach Eingang und Prüfung der Rücksendung — Bestätigung per E-Mail.'))
    }
    return out
  }

  root.querySelector('[data-action="submit"]').addEventListener('click', (e) => submit(e.currentTarget))

  async function submit(btn) {
    setBusy(btn, true)
    // Idempotenz-Schluessel beim ersten Submit-Klick erzeugen und mit dem
    // restlichen Wizard-State persistieren. Wenn der Browser zwischen POST
    // und renderResult einbricht (Hänger, Reload, Tab-Crash) und der Kunde
    // erneut auf "Anmelden" klickt, sendet er den gleichen Key — der Server
    // antwortet dann mit dem bereits angelegten Case statt einen zweiten
    // anzulegen.
    if (!state.idempotencyKey) {
      state.idempotencyKey = (crypto && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'k-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12)
      persistState()
    }
    const items = state.selectedItems.map((it) => {
      const det = state.exchangeDetails[it.orderLineId] || {}
      const photoUrls = state.photos[it.orderLineId] || []
      const reason = (state.warrantyReasons[it.orderLineId] || '').trim()
      return {
        orderLineId: it.orderLineId,
        quantity: it.quantity,
        exchangeTargetSku: det.exchangeTargetSku,
        exchangeMode: det.exchangeMode,
        photoUrls,
        reason: reason || undefined,
        // Bundle-Komponenten-Auswahl (Garantie/Falschlieferung): das Backend
        // legt je Komponente ein eigenes RmaCaseItem an statt des ganzen Bundles.
        isBundleComponent: it.isBundleComponent || undefined,
        productId: it.isBundleComponent ? it.productId : undefined,
        bundleResolutionId: it.isBundleComponent ? (it.bundleResolutionId || undefined) : undefined,
      }
    })
    const body = {
      channelKey: state.channelKey,
      type: state.type,
      identification: state.identification,
      selectedOrderId: state.order?.orderId,
      items,
      amazonContact: state.channelKey === 'amazon' ? state.amazonContact : undefined,
      shopifyExchangeAddress: state.channelKey !== 'amazon' && state.shopifyExchangeAddress ? state.shopifyExchangeAddress : undefined,
      // Neue Top-Level-Faelle: Backend entscheidet selbst ueber Label/keine-Retoure
      // (auto-decide). Resolution + Wrong-Delivery-Details mitgeben.
      shippingOption: isNewFlowType(state.type) ? 'auto-decide' : state.shippingOption,
      resolution: isNewFlowType(state.type) ? state.resolution || undefined : undefined,
      wrongDeliveryDescription: state.type === 'WRONG_DELIVERY' && state.wrongDeliveryDescription
        ? state.wrongDeliveryDescription : undefined,
      caseLevelPhotoUrls: state.type === 'WRONG_DELIVERY' && state.caseLevelPhotos.length > 0
        ? state.caseLevelPhotos : undefined,
      idempotencyKey: state.idempotencyKey,
    }
    const r = await post('/apps/rma/api/case.create', body)
    setBusy(btn, false)
    if (!r.ok) {
      if (r.error === 'deadline_passed') {
        const msg = r.type === 'WARRANTY'
          ? (STR.err_deadline_warranty || 'Die Garantiefrist von 2 Jahren ab Versand ist leider abgelaufen.')
          : (STR.err_deadline_general || 'Die Rückgabefrist von 40 Tagen ab Versand ist leider abgelaufen.')
        alert(msg + '\n\n' + (STR.err_deadline_contact || 'Bei Fragen wende dich gerne an support@getdigital.de.'))
        return
      }
      if (r.error === 'fba_not_supported') {
        alert(r.message || STR.err_fba_not_supported || 'Diese Bestellung wurde direkt von Amazon (Fulfilled by Amazon) versandt. Bitte wende dich direkt an den Amazon-Kundenservice.')
        return
      }
      if (r.error === 'exchange_mode_missing') {
        // Der Server hat den Umtausch abgelehnt, weil zu keinem Artikel ein
        // Umtausch-Modus (Gutschein/Größe) ankam. Das passiert, wenn der Wizard
        // aus einem wiederhergestellten/abgebrochenen Session-State ohne
        // durchlaufene Auswahl submittet. Stale-Auswahl verwerfen und zurück zur
        // Item-Auswahl — 'Weiter' setzt dann via next-from-items den Modus neu.
        state.exchangeDetails = {}
        alert(STR.err_exchange_mode_missing || 'Bitte wähle für deinen Umtausch noch aus, ob du einen Gutschein oder eine andere Größe möchtest.')
        show('SELECT_ITEMS')
        return
      }
      alert(STR.err_generic || 'Es ist ein Fehler aufgetreten. Bitte versuche es später erneut oder schreibe an support@getdigital.de.')
      return
    }
    renderResult(r)
  }

  function renderResult(r) {
    state.step = 'RESULT'
    render()
    // Vorgang ist abgeschickt — persistierten State löschen, damit ein
    // späterer Reload den Wizard wieder bei IDENTIFY startet.
    clearPersistedState()
    const wrap = root.querySelector('[data-target="result"]')
    const parts = []

    // Intro + Vorgangsnummer
    parts.push(`<p>${escapeHtml(STR.result_intro || 'Wir haben deinen Vorgang aufgenommen.')}</p>`)
    parts.push(`<p><strong>${escapeHtml(STR.result_label_case || 'Vorgangsnummer')}:</strong></p>`)
    parts.push(`<div class="rma-result__case">${escapeHtml(r.caseNumber)}</div>`)

    const isManualReview = r.status === 'MANUAL_REVIEW'
    const isAutoRefund = r.postAction === 'auto-refund' || state.autoRefund
    const hasLabel = !!r.dhlLabelUrl
    const isSelfReturn = !!r.returnAddress

    // Szenarien:
    //   I) MANUAL_REVIEW (überschreibt alles)
    //  II) Auto-Refund (Garantie EK<5/8 €)
    // III) Versandanleitung (DHL-Label oder Selbstrücksendung)
    //      + Zukunftsversprechen je nach type/exchangeMode (bei EXCHANGE eine
    //        Zusage pro gewähltem Per-Artikel-Modus)
    //  IV) Neue Faelle (WRONG_DELIVERY/MISSING_ITEM/PACKAGE_LOST)

    // PACKAGE_LOST landet immer in MANUAL_REVIEW, hat aber einen eigenen
    // freundlicheren Text als der generische MANUAL_REVIEW-Block.
    if (state.type === 'PACKAGE_LOST') {
      parts.push(`<p>${escapeHtml(STR.result_package_lost || 'Wir kümmern uns: zuerst fragen wir bei DHL nach, was mit deinem Paket passiert ist. Sobald wir Bescheid wissen, melden wir uns bei dir.')}</p>`)
      parts.push(`<p class="rma-result__footer">${escapeHtml(STR.result_support_footer || 'Bei Fragen wende dich gerne an support@getdigital.de.')}</p>`)
      wrap.innerHTML = parts.join('')
      return
    }

    if (state.type === 'WRONG_DELIVERY') {
      // Falschlieferung: ausnahmslos manuelle Bearbeitung. Wir zeigen nur,
      // dass wir uns zeitnah melden — keine Aufforderung zur Ruecksendung und
      // keine Zusage, was konkret passiert (Backend → MANUAL_REVIEW + CS-Notiz).
      parts.push(`<p>${escapeHtml(STR.result_manual_review_new_flow || STR.result_manual_review || 'Wir prüfen deinen Fall und melden uns zeitnah bei dir.')}</p>`)
      parts.push(`<p class="rma-result__footer">${escapeHtml(STR.result_support_footer || 'Bei Fragen wende dich gerne an support@getdigital.de.')}</p>`)
      wrap.innerHTML = parts.join('')
      return
    }

    if (isNewFlowType(state.type)) {
      // MISSING_ITEM: Backend kann zurueckgeben:
      // - status=MANUAL_REVIEW (Hochpreis-Schwelle) → freundlicher MR-Text
      // - postAction=new-flow + dhlLabelUrl → Retoure-Label, Ersatz nach Erstscan
      // - postAction=new-flow ohne Label → sofortige Aktion
      if (isManualReview) {
        parts.push(`<p>${escapeHtml(STR.result_manual_review_new_flow || STR.result_manual_review || 'Wir prüfen deinen Fall manuell und melden uns innerhalb weniger Werktage per E-Mail bei dir.')}</p>`)
      } else if (hasLabel) {
        // Label noetig — gleicher DHL-Block wie bei RETURN/EXCHANGE.
        parts.push(`<p><strong>${escapeHtml(STR.result_next_steps || 'So geht’s weiter:')}</strong></p>`)
        parts.push(`<p>${r.dropOffHint ? escapeHtml(r.dropOffHint).replace(/\n/g, '<br>') : escapeHtml(STR.result_dhl_steps || 'Verpacke die Ware sicher. Klebe das DHL-Retourelabel auf — oder zeig den QR-Code direkt am Smartphone vor. Gib das Paket bei DHL ab oder mit beim DHL-Fahrer mit. Bei der Filiale oder ausgewählten Packstationen geht beides.')}</p>`)
        parts.push(`<p><a href="${escapeHtml(r.dhlLabelUrl)}" target="_blank" rel="noopener">${escapeHtml(STR.result_download_label || 'Versandlabel herunterladen')}</a></p>`)
        const promise = state.resolution === 'REPLACEMENT'
          ? (STR.result_replacement_after_label || 'Sobald wir die Übergabe deiner Rücksendung an DHL registrieren, schicken wir dir die Ware nach Prüfung erneut zu — Tracking per E-Mail.')
          : (STR.result_refund_after_label || 'Sobald wir die Übergabe deiner Rücksendung an DHL registrieren, erstatten wir dir den Kaufbetrag — meist innerhalb weniger Tage.')
        parts.push(`<p>${escapeHtml(promise)}</p>`)
      } else {
        // Kein Label — entweder darf der Kunde behalten (WRONG_DELIVERY,
        // EK<150% Labelkosten) oder es ist ein MISSING_ITEM (nichts zurueck).
        const promise = state.type === 'WRONG_DELIVERY'
          ? (state.resolution === 'REPLACEMENT'
              ? (STR.result_replacement_keep || 'Die falsche Lieferung darfst du als kleine Entschuldigung behalten — wir schicken dir die richtige Ware in Kürze nochmal zu.')
              : (STR.result_refund_keep || 'Wir erstatten dir den Kaufbetrag in Kürze — die falsche Lieferung darfst du als kleine Entschuldigung behalten.'))
          : (state.resolution === 'REPLACEMENT'
              ? (STR.result_replacement_missing || 'Wir schicken dir den fehlenden Artikel in Kürze nach — Tracking per E-Mail, sobald die Sendung unterwegs ist.')
              : (STR.result_refund_missing || 'Wir erstatten dir den Kaufbetrag des fehlenden Artikels in Kürze — eine Bestätigung kommt per E-Mail.'))
        parts.push(`<p>${escapeHtml(promise)}</p>`)
      }
      parts.push(`<p class="rma-result__footer">${escapeHtml(STR.result_support_footer || 'Bei Fragen wende dich gerne an support@getdigital.de.')}</p>`)
      wrap.innerHTML = parts.join('')
      return
    }

    if (isManualReview) {
      parts.push(`<p>${escapeHtml(STR.result_manual_review || 'Wir prüfen deinen Fall manuell und melden uns innerhalb weniger Werktage per E-Mail bei dir.')}</p>`)
    } else if (isAutoRefund) {
      parts.push(`<p>${escapeHtml(STR.result_auto_refund || 'Wir haben dir den Kaufbetrag bereits auf deine ursprüngliche Zahlungsmethode erstattet — eine Bestätigung kommt per E-Mail. Du musst nichts zurückschicken. Vielen Dank!')}</p>`)
    } else {
      // Versand-Anleitung
      if (hasLabel) {
        parts.push(`<p><strong>${escapeHtml(STR.result_next_steps || 'So geht’s weiter:')}</strong></p>`)
        parts.push(`<p>${r.dropOffHint ? escapeHtml(r.dropOffHint).replace(/\n/g, '<br>') : escapeHtml(STR.result_dhl_steps || 'Verpacke die Ware sicher. Klebe das DHL-Retourelabel auf — oder zeig den QR-Code direkt am Smartphone vor. Gib das Paket bei DHL ab oder mit beim DHL-Fahrer mit. Bei der Filiale oder ausgewählten Packstationen geht beides.')}</p>`)
        parts.push(`<p><a href="${escapeHtml(r.dhlLabelUrl)}" target="_blank" rel="noopener">${escapeHtml(STR.result_download_label || 'Versandlabel herunterladen')}</a></p>`)
      } else if (isSelfReturn) {
        parts.push(`<p>${escapeHtml(STR.result_return_address || 'Bitte schicke deine Rücksendung an folgende Adresse:')}</p>`)
        parts.push(`<address>${escapeHtml(r.returnAddress.name)}<br>${escapeHtml(r.returnAddress.street)}<br>${escapeHtml(r.returnAddress.zip)} ${escapeHtml(r.returnAddress.city)}<br>${escapeHtml(r.returnAddress.country)}</address>`)
        parts.push(`<p>${escapeHtml(STR.result_self_return_costs || 'Versandkosten trägst du selbst — wir empfehlen einen versicherten Versand mit Sendungsverfolgung.')}</p>`)
      }

      // Zukunftsversprechen je Case-Typ
      if (state.type === 'RETURN') {
        parts.push(`<p>${escapeHtml(hasLabel
          ? (STR.result_return_promise_first_scan || 'Sobald wir die Übergabe an DHL registrieren, erstatten wir dir den Kaufbetrag — meist innerhalb weniger Tage. Du bekommst eine Bestätigung per E-Mail.')
          : (STR.result_return_promise_arrival || 'Nach Empfang der Ware erstatten wir dir den Kaufbetrag. Du bekommst eine Bestätigung per E-Mail.'))}</p>`)
      } else if (state.type === 'EXCHANGE') {
        // Eine Zusage je gewähltem Per-Artikel-Modus — ein Vorgang kann
        // Größentausch, Gutschein und Erstattung gleichzeitig enthalten.
        for (const promise of exchangePromises(hasLabel)) {
          parts.push(`<p>${escapeHtml(promise)}</p>`)
        }
      } else if (state.type === 'WARRANTY') {
        parts.push(`<p>${escapeHtml(STR.result_warranty_arrival || 'Nach Empfang und Prüfung bearbeiten wir den Garantiefall — du erhältst Ersatz, Reparatur oder Erstattung. Wir melden uns per E-Mail.')}</p>`)
      }
    }

    // Footer
    parts.push(`<p class="rma-result__footer">${escapeHtml(STR.result_support_footer || 'Bei Fragen wende dich gerne an support@getdigital.de.')}</p>`)

    wrap.innerHTML = parts.join('')
  }

  // ── Back-Buttons ───────────────────────────────────────────
  root.querySelectorAll('[data-action="back"]').forEach((btn) => btn.addEventListener('click', back))

  // „Anderen Fall anmelden" — verwerfe den persistierten State und lade
  // die Seite neu, damit der Wizard sauber von vorn beginnt.
  root.querySelectorAll('[data-action="restart"]').forEach((btn) => btn.addEventListener('click', () => {
    isRestarting = true
    clearPersistedState()
    window.location.reload()
  }))

  // ── Utils ──────────────────────────────────────────────────
  function escapeHtml(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }
})()
